import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, LogOut, Wallet } from 'lucide-react'
import { useSidebar } from '@/hooks/useSidebar'
import { useAuthStore } from '@/lib/auth-store'
import { authService } from '@/services/auth.service'
import { apiService } from '@/services/api.service'
import { navigationConfig } from '@/config/navigation'
import { LogoutConfirmModal } from '@/components/auth/LogoutConfirmModal'
import { cn, formatCurrency } from '@/lib/utils'
import { Logo } from '@/components/ui/Logo'

export function Sidebar() {
  const location = useLocation()
  const { isCollapsed, toggleCollapsed } = useSidebar()
  const { user } = useAuthStore()
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [avatarError, setAvatarError] = useState(false)

  // Fetch balance from API (compartilha cache com Dashboard)
  const { data: balance } = useQuery({
    queryKey: ['balance'],
    queryFn: () => apiService.fetchBalance(),
    refetchInterval: 5 * 60 * 1000,
  })

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard'
    }
    return location.pathname.startsWith(href)
  }

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-10 hidden h-screen transition-all duration-300 ease-out lg:flex',
        isCollapsed ? 'lg:w-[72px]' : 'lg:w-60',
      )}
    >
      <div className="relative flex h-full w-full flex-col bg-sidebar border-r border-sidebar-border/30">
        {/* Header */}
        <div className="flex h-14 items-center justify-between px-3">
          {!isCollapsed && (
            <Link to="/dashboard" className="flex items-center transition-opacity hover:opacity-80">
              <Logo className="text-sidebar-foreground" />
            </Link>
          )}
          <button
            onClick={toggleCollapsed}
            className={cn(
              'inline-flex items-center justify-center h-8 w-8 rounded-lg',
              'bg-sidebar-accent/50 border border-sidebar-border/30',
              'transition-all duration-200 hover:bg-sidebar-accent',
              'text-sidebar-foreground/70 hover:text-sidebar-foreground',
              isCollapsed && 'mx-auto',
            )}
          >
            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        {/* Divider */}
        <div className="mx-3 h-px bg-gradient-to-r from-transparent via-sidebar-border/50 to-transparent" />

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden p-3">
          {isCollapsed ? (
            /* Collapsed Mode - Icons Only */
            <div className="space-y-1">
              {navigationConfig.flatMap((section) =>
                section.items.map((item) => {
                  const Icon = item.icon
                  const active = isActive(item.href)
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn(
                        'group relative flex items-center justify-center h-10 w-10 rounded-xl mx-auto',
                        'transition-all duration-200',
                        active
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50',
                      )}
                      title={item.name}
                    >
                      <Icon className="h-[18px] w-[18px]" />
                      {active && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] bg-primary rounded-r-full" />
                      )}
                    </Link>
                  )
                }),
              )}
            </div>
          ) : (
            /* Expanded Mode */
            <div className="space-y-6">
              {navigationConfig.map((section) => (
                <div key={section.name}>
                  {/* Section Label */}
                  <div className="px-3 mb-2">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-sidebar-foreground/40">
                      {section.name}
                    </span>
                  </div>

                  {/* Section Items */}
                  <div className="space-y-0.5">
                    {section.items.map((item) => {
                      const Icon = item.icon
                      const active = isActive(item.href)
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          className={cn(
                            'group relative flex items-center gap-3 rounded-xl px-3 py-2.5',
                            'text-[13px] transition-all duration-200',
                            active
                              ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                              : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50',
                          )}
                        >
                          <Icon
                            className={cn(
                              'h-[18px] w-[18px] shrink-0 transition-colors',
                              active ? 'text-sidebar-accent-foreground' : 'text-sidebar-foreground/50 group-hover:text-sidebar-foreground/80',
                            )}
                          />
                          <span className="truncate">{item.name}</span>
                          {active && (
                            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] bg-primary rounded-r-full" />
                          )}
                        </Link>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className="p-3 space-y-3">
          {/* Frase Motivacional */}
          {!isCollapsed && (
            <div className="px-2 py-3 rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/10">
              <p className="text-[10px] font-medium text-center leading-relaxed text-sidebar-foreground/60 uppercase tracking-wide">
                O mercado premia a consistência,
                <br />
                <span className="text-primary font-semibold">nunca desista!</span>
              </p>
            </div>
          )}

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-sidebar-border/50 to-transparent" />

          {/* Saldo da Carteira */}
          {!isCollapsed ? (
            <div className="px-2 py-3 rounded-xl bg-sidebar-accent/30 border border-sidebar-border/30">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="h-4 w-4 text-primary/70" />
                <span className="text-[10px] font-medium text-sidebar-foreground/50 uppercase tracking-wide">
                  Saldo Disponível
                </span>
              </div>
              <p className="text-lg font-bold text-primary">
                {formatCurrency(balance || 0)}
              </p>
            </div>
          ) : (
            <div
              className="flex items-center justify-center h-10 w-10 mx-auto rounded-xl bg-sidebar-accent/30 border border-sidebar-border/30"
              title={`Saldo: ${formatCurrency(balance || 0)}`}
            >
              <Wallet className="h-5 w-5 text-primary" />
            </div>
          )}

          {user && (
            <div
              className={cn(
                'flex items-center rounded-xl transition-colors',
                !isCollapsed && 'gap-3 p-2 hover:bg-sidebar-accent/30',
              )}
            >
              {!isCollapsed ? (
                <>
                  {/* Avatar */}
                  {user.avatar && !avatarError ? (
                    <img
                      src={user.avatar}
                      alt={user.nickname || user.name || 'Avatar'}
                      className="h-9 w-9 rounded-full object-cover shrink-0"
                      onError={() => setAvatarError(true)}
                    />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary shrink-0">
                      {(user.nickname || user.name || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}

                  {/* User Info */}
                  <div className="flex-1 overflow-hidden min-w-0">
                    <p className="truncate text-sm font-medium text-sidebar-foreground">
                      {user.nickname || user.name}
                    </p>
                    <p className="truncate text-[11px] text-sidebar-foreground/50">
                      {user.email}
                    </p>
                  </div>

                  {/* Logout Button */}
                  <button
                    onClick={() => setShowLogoutModal(true)}
                    className="p-2 rounded-lg text-sidebar-foreground/40 hover:text-white hover:bg-destructive/80 transition-colors shrink-0"
                    title="Sair"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowLogoutModal(true)}
                  className="flex h-10 w-10 items-center justify-center rounded-xl mx-auto hover:opacity-80 transition-opacity overflow-hidden"
                  title="Sair"
                >
                  {user.avatar && !avatarError ? (
                    <img
                      src={user.avatar}
                      alt={user.nickname || user.name || 'Avatar'}
                      className="h-10 w-10 rounded-xl object-cover"
                      onError={() => setAvatarError(true)}
                    />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-sm font-semibold text-primary">
                      {(user.nickname || user.name || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <LogoutConfirmModal
        open={showLogoutModal}
        onOpenChange={setShowLogoutModal}
        onConfirm={() => authService.logout()}
      />
    </aside>
  )
}
